import {
  Button,
  Checkbox,
  type CheckboxProps,
  Divider,
  Flex,
  Menu,
  Pagination,
  type PaginationProps,
  Text,
} from "@mantine/core";
import { Grid } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useParams, useSearchParams } from "react-router-dom";
import HistoricalSnapshotButton from "src/components/pages/HistoricalSnapshot/HistoricalSnapshotButton";
import { BackButton } from "src/components/shared/BackButton/BackButton";
import CheckListsButton from "src/generator/LogBar/_Buttons/CheckListsButton";
import FilesButton from "src/generator/LogBar/_Buttons/FilesButton";
import HistoryButton from "src/generator/LogBar/_Buttons/HistoryButton";
import { PendingFilesProvider } from "src/generator/LogBar/PendingFilesContext";
import { useCheckPerm } from "src/hooks";
import { type HistoricalSnapshotProps } from "src/pages/Summary/Summary.hooks";
import { StyledBurger } from "src/styled";

import VerifyButton from "../VerifyButton";
import { StyledGridCol } from "../VerifyEntries/VerifyEntries.styled";
import ActionRequired from "./ActionRequired/ActionRequired";

interface Props {
  paginationProps: PaginationProps;
  checkboxProps: CheckboxProps;
  historicalSnapshotProps: HistoricalSnapshotProps;
  verifiable: boolean;
  lbname: string;
  deleted: number;
  status: string;
  audit?: boolean;
  expired?: boolean;
  isLoading: boolean;
}

const ViewReviewHeader = ({
  verifiable,
  paginationProps,
  checkboxProps,
  lbname,
  audit,
  expired,
  historicalSnapshotProps,
}: Props) => {
  const [opened, { toggle }] = useDisclosure(false);
  const [openedActionRequired, { open, close }] = useDisclosure(false);
  const { aid } = useParams();
  const [searchParams] = useSearchParams();
  const checkPerm = useCheckPerm();

  return (
    <>
      <ActionRequired opened={openedActionRequired} close={close} />
      <Grid m={0} p={0} pb={10}>
        <StyledGridCol align="center" justify="start" span={4}>
          <BackButton />
          <Text ml={15}>
            {`${audit ? "Audit" : "Review"} Entries - ${lbname}`}
          </Text>
        </StyledGridCol>
        <StyledGridCol align="center" justify="end" span={8}>
          <Pagination {...paginationProps} />
          <Flex ml="xl" align="center" gap={12}>
            <PendingFilesProvider>
              <Checkbox
                labelPosition="left"
                label="Verify"
                disabled={expired || !verifiable}
                {...checkboxProps}
              />

              {!expired && (
                <>
                  <VerifyButton
                    request={"review-verify-entry"}
                    navigatePath={`/summary/${
                      audit ? "audits/audit" : "reviews/review"
                    }/${aid}`}
                  />

                  <Button variant="outline" onClick={open}>
                    Action Required
                  </Button>
                  {(checkPerm("viewfiles") ||
                    checkPerm("viewhistory") ||
                    searchParams.get("preview")) && (
                    <Menu
                      keepMounted
                      onClose={toggle}
                      shadow="md"
                      width={200}
                      position="bottom-end">
                      <Menu.Target>
                        <StyledBurger opened={opened} onClick={toggle} />
                      </Menu.Target>
                      <Menu.Dropdown>
                        <FilesButton hamburger />
                        <CheckListsButton />
                        <HistoricalSnapshotButton
                          {...historicalSnapshotProps}
                        />
                        <HistoryButton hamburger summary />
                      </Menu.Dropdown>
                    </Menu>
                  )}
                </>
              )}
            </PendingFilesProvider>
          </Flex>
        </StyledGridCol>
        <StyledGridCol align="center" justify="center" span={12}>
          <Divider w="100%" />
        </StyledGridCol>
        <StyledGridCol align="center" justify="center" span={12}>
          <Text size="lg" fw="bold">
            {lbname}
          </Text>
        </StyledGridCol>
      </Grid>
    </>
  );
};

export default ViewReviewHeader;
